import React from "react";
import useDepartmentDoctors from "../hooks/useDepartmentDoctors";
import DoctorCard from "./DoctorCard";
import { Link } from "react-router-dom"
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"
import {
  Heart,
  Phone,
  Mail,
  MapPin,
  Activity,
  Award,
  Clock,
  Users,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
} from "lucide-react";

function PlasticSurgeryPage() {
  const { open: openAppointment } = useAppointment();
  const doctors = useDepartmentDoctors("Plastic Surgery");

  const subSpecialties = [
    // {
    //   name: "Reconstructive Surgery",
    //   description: "Restoration after trauma, burns, or cancer surgery",
    //   image: "/ReconstructiveSurgery.jpg",
    // },
    {
      name: "Cosmetic Surgery",
      description: "Procedures to enhance appearance and self-esteem",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Cosmetic-Surgery-scaled.jpg",
    },
    {
      name: "Microvascular Surgery",
      description: "Advanced tissue and nerve reconstruction",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Microvascular-Surgery-scaled.jpg",
    },
    {
      name: "Burn Care & Scar Revision",
      description: "Comprehensive management of burns and scars",
      image: "/BurnCare&ScarRevision.jpg",
    },
    {
      name: "Hand & Facial Reconstruction",
      description: "Functional and cosmetic restoration for delicate areas",
      image: "/Hand&FacialReconstruction.jpg",
    },
  ];

  const treatments = [
    { name: "Skin Grafting & Flap Surgery", icon: Activity },
    { name: "Facial Fracture & Hand Injury Repair", icon: Heart },
    { name: "Burn Reconstruction & Scar Revision", icon: Activity },
    { name: "Liposuction & Body Contouring", icon: Heart },
    { name: "Breast Augmentation, Reduction & Reconstruction", icon: Activity },
    { name: "Cosmetic Rhinoplasty & Blepharoplasty", icon: Heart },
  ];

  const whyChoose = [
    {
      title: "Expert Surgeons",
      description: "Highly skilled reconstructive & cosmetic surgeons",
      icon: Users,
    },
    {
      title: "Advanced Technology",
      description: "Latest microvascular and laser technologies",
      icon: Activity,
    },
    {
      title: "24x7 Care",
      description: "Round-the-clock trauma and burn care",
      icon: Clock,
    },
    {
      title: "Personalized Support",
      description: "Pre- and post-surgery comprehensive care",
      icon: Heart,
    },
    {
      title: "Accessible Location",
      description: "Located near Miyapur & Bachupally",
      icon: MapPin,
    },
  ];

  const articles = [
    { title: "Difference Between Cosmetic and Reconstructive Surgery", emoji: "💎" },
    { title: "Scar Management After Burns", emoji: "🔥" },
    { title: "How Plastic Surgery Restores Function and Beauty", emoji: "🩺" },
    { title: "What to Expect Before and After Cosmetic Procedures", emoji: "💫" },
  ];

  const faqs = [
    {
      question: "Is plastic surgery only for aesthetics?",
      answer: "No, it includes reconstructive surgeries that restore form and function after trauma or disease.",
    },
    {
      question: "Are cosmetic procedures safe?",
      answer: "Yes, when performed by qualified surgeons in a hospital setting like MAMS, they are very safe.",
    },
    {
      question: "What is the recovery time after plastic surgery?",
      answer: "It varies by procedure — from a few days to a few weeks.",
    },
  ];
const schema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "MAMS Hospitals Plastic Surgery Department",
    "url": "https://mamshospitals.com/plastic-surgery",
    "logo": "https://mamshospitals.com/MAMS-Logo.png",
    "image": "https://mamshospitals.com/PlasticSurgery.jpg",
    "description": "MAMS Hospitals provides advanced plastic and reconstructive surgery in Hyderabad including cosmetic surgery, burn reconstruction, scar revision, microvascular surgery, and facial reconstruction.",
    "medicalSpecialty": "Plastic Surgery",
    "telephone": "+91-7399999981",
    "priceRange": "$$",

    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bachupally",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500090",
      "addressCountry": "IN"
    },

    "availableService": [
      {
        "@type": "MedicalProcedure",
        "name": "Cosmetic Surgery"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Scar Revision Surgery"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Burn Reconstruction"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Microvascular Surgery"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Facial Reconstruction Surgery"
      }
    ],

    "sameAs": [
      "https://www.facebook.com/MAMSHospitals",
      "https://www.instagram.com/mams_hospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Is plastic surgery only for cosmetic purposes?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No, plastic surgery also includes reconstructive procedures that restore function and appearance after trauma, burns, or surgery."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide burn reconstruction surgery?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals offers advanced burn reconstruction and scar revision procedures."
        }
      },
      {
        "@type": "Question",
        "name": "Are cosmetic surgeries safe?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, cosmetic procedures performed by experienced plastic surgeons in a hospital setting are generally safe and effective."
        }
      }
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Plastic Surgery Department - MAMS Hospitals",
    "url": "https://mamshospitals.com/plastic-surgery",
    "description": "Best plastic surgery hospital in Hyderabad offering cosmetic surgery, reconstructive surgery, scar revision, burn care, and facial reconstruction.",
    "keywords": [
      "Plastic Surgery Hospital Hyderabad",
      "Best Plastic Surgeon Hyderabad",
      "Cosmetic Surgery Hyderabad",
      "Burn Reconstruction Hyderabad",
      "Scar Revision Surgery Hyderabad",
      "Facial Reconstruction Hyderabad",
      "Plastic Surgery Hospital Bachupally"
    ]
  }
];
  return (
    <>
    <SEO
    title="Best Plastic Surgery Hospital in Hyderabad | Cosmetic & Reconstructive Surgery | MAMS Hospitals"
    description="MAMS Hospitals offers advanced plastic surgery in Hyderabad including cosmetic surgery, burn reconstruction, scar revision, facial reconstruction, and microvascular surgery."
    keywords="plastic surgery hospital Hyderabad,
    best plastic surgeon Hyderabad,
    cosmetic surgery Hyderabad,
    reconstructive surgery Hyderabad,
    burn reconstruction Hyderabad,
    scar revision surgery Hyderabad,
    facial reconstruction Hyderabad,
    microvascular surgery Hyderabad,
    rhinoplasty Hyderabad,
    liposuction Hyderabad,
    plastic surgery hospital Bachupally,
    cosmetic surgeon Hyderabad,
    MAMS Hospitals Plastic Surgery"
    schema={schema}
    />

    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-white py-12 md:py-24">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Best Plastic Surgery Hospital in Hyderabad
              </div>

              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                Rebuilding Confidence, One Transformation at a Time
              </h1>

              <p className="text-lg text-gray-700 mb-8">
                At MAMS Hospitals, we combine artistry with surgical precision to enhance aesthetics and restore function.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
            </div>

            <div className="relative mt-8 md:mt-0 h-[300px] md:h-[400px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/PlasticSurgery.jpg"
                alt="Plastic Surgery at MAMS Hospital"
                className="w-full h-full object-cover"
              canonicalUrl="https://mamshospitals.com/departments/plasticsurgery"
      />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 p-4 rounded-lg shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="bg-primary/10 p-3 rounded-full">
                    <Award className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Certified Excellence</p>
                    <p className="text-sm text-gray-600">Leading plastic surgery center</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sub-Specialties */}
      <section className="py-16 md:py-20 px-0 bg-slate-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Sub-Specialties in Plastic Surgery
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive reconstructive and aesthetic surgery services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subSpecialties.map((s, i) => (
              <Card key={i} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="relative h-48">
                  <img src={s.image} alt={s.name} className="w-full h-full object-cover" />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{s.name}</h3>
                  <p className="text-gray-600">{s.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Treatments */}
      <section className="py-16 md:py-20 px-0">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Treatments & Procedures</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Advanced surgical techniques for reconstruction and aesthetic improvement
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {treatments.map((t, i) => {
              const Icon = t.icon;
              return (
                <Card key={i} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-primary/10 p-3 rounded-lg">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="font-semibold text-gray-900">{t.name}</h3>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 md:py-20 px-0 bg-primary/5">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose MAMS Hospitals for Plastic Surgery
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Excellence in surgical precision and patient care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((w, i) => {
              const Icon = w.icon;
              return (
                <Card key={i} className="bg-white hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{w.title}</h3>
                    <p className="text-gray-600">{w.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Specialist */}
                {doctors.length > 0 && (
          <section className="py-16 px-0 bg-apple-gray-50">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Meet Our Specialists</h2>
              </div>
              <div className="relative">
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: -340, behavior: 'smooth' }); }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div id="doc-scroll" className="flex gap-6 overflow-x-auto scroll-smooth px-10 scrollbar-hide pb-4">
                  {doctors.map(doctor => (
                    <div key={doctor.id} className="w-[280px] sm:w-[300px] flex-shrink-0">
                      <DoctorCard doctor={{
                        name: doctor.title.rendered,
                        slug: doctor.slug,
                        imageUrl: doctor._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
                        specialty: doctor._embedded?.["wp:term"]?.[0]?.[0]?.name || "",
                        designation: doctor.acf?.designation || "",
                        qualification: doctor.acf?.qualification || "",
                        experience: doctor.acf?.experience || "",
                      }} />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: 340, behavior: 'smooth' }); }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
              <div className="text-center mt-8">
                <Link to="/doctors">
                  <button className="px-6 py-3 rounded-full border text-sm font-medium inline-flex items-center gap-2">
                    View All Doctors
                  </button>
                </Link>
              </div>
            </div>
          </section>
          )}

      {/* FAQ */}
      <section className="py-16 md:py-20 px-0">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary" />
                    {faq.question}
                  </h3>
                  <p className="text-gray-700 ml-9">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>
                <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department</p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}

export default PlasticSurgeryPage;
